import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventsRepository } from '../services/repository';
import './TournamentCreationMenu.css';

const TournamentCreationMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [groupedEvents, setGroupedEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState('event'); // Default to event view

  useEffect(() => {
    if (isOpen) {
      loadEvents();
    }
  }, [isOpen]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const currentDate = new Date();
      const toDate = new Date();
      toDate.setDate(currentDate.getDate() + 5); // Add 2 days to current date
      
      // Fetch events with filters for americano, mexicano, nightGames types
      const typeFilters = ['americano', 'mexicano', 'nightGames'];
              const eventsData = await EventsRepository.fetchEvents({ 
        from: currentDate, 
        to: toDate,
        types: typeFilters 
      });
      
      // Sort events by date in ascending order (using seconds since 1970)
      const sortedEvents = (eventsData.events || []).sort((a, b) => {
        const dateA = parseInt(a.startDate); // Already in seconds since 1970
        const dateB = parseInt(b.startDate); // Already in seconds since 1970
        return dateA - dateB; // Ascending order
      });
      
      setEvents(sortedEvents);
      
      // Group events by date
      const grouped = groupEventsByDate(sortedEvents);
      setGroupedEvents(grouped);
    } catch (error) {
      console.error('Error loading events:', error);
      alert('Błąd ładowania wydarzeń');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromEvent = (event) => {
    // Store event details in localStorage and navigate to tournament creator
    const eventDetails = {
      ...event.details,
      type: event.type // Include the event type
    };
    window.localStorage.setItem('eventDetails', JSON.stringify(eventDetails));
    onClose();
    navigate('/tournament-creator');
  };

  const handleCreateEmpty = () => {
    // Clear any existing event details and navigate to tournament creator
    window.localStorage.removeItem('eventDetails');
    onClose();
    navigate('/tournament-creator');
  };

  const formatDate = (startDate, endDate) => {
    // Convert seconds since 1970 to Date object
    const start = new Date(parseInt(startDate) * 1000);
    const end = new Date(parseInt(endDate) * 1000);
    
    const dateStr = start.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    
    const startTime = start.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const endTime = end.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return `${dateStr} ${startTime}-${endTime}`;
  };

  const formatDayHeader = (dateString) => {
    // Convert seconds since 1970 to Date object
    const date = new Date(parseInt(dateString) * 1000);
    
    // Format as "EEE dd.MM" (e.g., "Pon 15.01")
    const dayName = date.toLocaleDateString('pl-PL', { weekday: 'short' });
    const dayMonth = date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit'
    });
    
    return `${dayName} ${dayMonth}`;
  };

  const groupEventsByDate = (events) => {
    const grouped = {};
    
    events.forEach(event => {
      const dateKey = new Date(parseInt(event.startDate) * 1000).toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });
    
    // Sort events within each group by start time
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => {
        const timeA = new Date(parseInt(a.startDate) * 1000).getTime();
        const timeB = new Date(parseInt(b.startDate) * 1000).getTime();
        return timeA - timeB;
      });
    });
    
    // Convert to array and sort by date
    return Object.entries(grouped)
      .sort(([dateKeyA], [dateKeyB]) => {
        const dateA = new Date(dateKeyA).getTime();
        const dateB = new Date(dateKeyB).getTime();
        return dateA - dateB;
      })
      .map(([dateKey, events]) => ({
        dateKey,
        dateString: new Date(dateKey).toDateString(),
        events
      }));
  };

  const formatTime = (dateString) => {
    // Convert seconds since 1970 to Date object
    const date = new Date(parseInt(dateString) * 1000);
    return date.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBadgeClass = (attendees, maxAttendees) => {
    if (maxAttendees === 0) return 'low';
    const percentage = (attendees / maxAttendees) * 100;
    
    if (percentage >= 100) return 'full';
    if (percentage >= 50) return 'high';
    return 'low';
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  console.log('TournamentCreationMenu render:', { isOpen, eventsCount: events.length });
  
  if (!isOpen) {
    console.log('Modal is not open, returning null');
    return null;
  }
  
  console.log('Modal is open, rendering with events:', events.length);

  return (
    <div className="tournament-creation-modal-backdrop" onClick={handleBackdropClick}>
      <div className="tournament-creation-modal">
        <div className="modal-header">
          <h2 className="modal-title">Utwórz nowy turniej</h2>
        </div>
        
        <div className="modal-body">
          {/* Events List */}
          <div className="events-section">
            <h4>Dostępne wydarzenia:</h4>
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Ładowanie...</span>
                  </div>
                </div>
              ) : events.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-calendar-x fs-1"></i>
                  <p className="mt-2">Brak dostępnych wydarzeń</p>
                </div>
              ) : (
                                <div className="events-list">
                  {groupedEvents.map((group) => (
                    <div key={group.dateKey} className="day-group">
                      <h5 className="day-header">
                        {formatDayHeader(group.events[0].startDate)}
                      </h5>
                      {group.events.map((event) => (
                        <div key={event.id} className="event-card" onClick={() => handleCreateFromEvent(event)}>
                          <div className="event-info">
                            <h5 className="event-title">{event.name}</h5>
                            <div className="event-time">
                              Godzina: {formatTime(event.startDate)} - {formatTime(event.endDate)}
                            </div>
                          </div>
                          
                          {/* Participant count badge in top right */}
                          <div className={`participant-badge ${getBadgeClass(event.details?.attendees || 0, event.details?.maxAttendees || 0)}`}>
                            {event.details?.attendees || 0}/{event.details?.maxAttendees || 0}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>


        </div>
        
        <div className="modal-footer">
          <button type="button" className="btn btn-primary" onClick={handleCreateEmpty}>
            <i className="bi bi-plus-circle me-2"></i>
            Pusty
          </button>
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
};

export default TournamentCreationMenu;
